import { DocumentUpload, Warning2 } from "iconsax-react";
import { Button, Form, Input, Select, Upload, Empty } from "antd";
import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  getStorage,
  ref as sRef,
  uploadBytes,
  getDownloadURL,
} from "firebase/storage";
import Swal from "sweetalert2";
import { useAuth } from "../../../hooks/auth";
import { useSinalario } from "../../../hooks/sinalario";

import MobileLayout from "../../../components/MobileLayout";
import {
  Main,
  FormHeader,
  FormSection,
  MediaUploadSection,
  MediaPreviewSquare,
  FormFooter,
} from "./styles";

export interface FormProps {
  uid: string;
  descricao: string;
  image: string;
  letra: string;
  palavra: string;
  video: string;
  categoria: string;
}

function geraStringAleatoria(tamanho: number) {
  let stringAleatoria = "";
  const caracteres =
    "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
  for (let i = 0; i < tamanho; i += 1) {
    stringAleatoria += caracteres.charAt(
      Math.floor(Math.random() * caracteres.length)
    );
  }
  return stringAleatoria;
}

function EditVerbete() {
  const { TextArea } = Input;
  const { Option } = Select;
  const { editVerbete, loadingVerbetes } = useSinalario();
  const { state } = useLocation();
  const navigate = useNavigate();
  const formRef = useRef<any>(null);
  const { user } = useAuth();
  // eslint-disable-next-line prefer-destructuring
  const uid = state?.uid;
  const [newImage, setNewImage] = useState(state?.image || "");
  const [newVideo, setNewVideo] = useState(state?.video || "");

  const handleEditVerbete = useCallback(
    async ({ descricao, letra, palavra, categoria }: FormProps) => {
      try {
        await editVerbete({
          uid,
          image: newImage,
          letra,
          descricao,
          video: newVideo,
          palavra,
          categoria: categoria || "",
        });

        navigate("/sinalario/list");
      } catch (error) {
        console.log(error);
      }
    },
    [editVerbete, uid, newImage, newVideo, navigate]
  );

  const customUploadImage = async ({ file, onSuccess, onError }: any) => {
    const storage = getStorage();
    const metadata = { contentType: "image/jpeg" };
    const nick = geraStringAleatoria(16);
    if (!user?.uid) return;
    const fileName = user.uid;
    const imgFile = sRef(storage, `sinalario/images/${fileName + nick}.png`);

    Swal.fire({
      title: "Realizando envio da imagem...",
      showConfirmButton: false,
      allowEscapeKey: false,
      allowOutsideClick: false,
      didOpen: async () => {
        Swal.showLoading();
        try {
          await uploadBytes(imgFile, file, metadata);
          const downloadUrl = await getDownloadURL(imgFile);
          setNewImage(downloadUrl);
          if (onSuccess) onSuccess("ok");
          Swal.close();
          Swal.fire({ icon: "success", showConfirmButton: false, timer: 1000 });
        } catch (e) {
          Swal.fire({
            icon: "error",
            title: "Oops...",
            text: "Aconteceu algum erro inesperado.\nTente novamente.",
          });
          console.log(e);
          if (onError) onError(e);
        }
      },
    });
  };

  const customUploadVideo = async ({ file, onSuccess, onError }: any) => {
    const storage = getStorage();
    const metadata = { contentType: file.type || "video/mp4" };
    const nick = geraStringAleatoria(16);
    if (!user?.uid) return;
    const fileName = user.uid;
    const videoFile = sRef(storage, `sinalario/videos/${fileName + nick}.mp4`);

    Swal.fire({
      title: "Realizando envio do vídeo...",
      showConfirmButton: false,
      allowEscapeKey: false,
      allowOutsideClick: false,
      didOpen: async () => {
        Swal.showLoading();
        try {
          await uploadBytes(videoFile, file, metadata);
          const downloadUrl = await getDownloadURL(videoFile);
          setNewVideo(downloadUrl);
          if (onSuccess) onSuccess("ok");
          Swal.close();
          Swal.fire({ icon: "success", showConfirmButton: false, timer: 1000 });
        } catch (e) {
          Swal.fire({
            icon: "error",
            title: "Oops...",
            text: "Aconteceu algum erro com o upload do vídeo.\nTente novamente.",
          });
          console.log(e);
          if (onError) onError(e);
        }
      },
    });
  };

  useEffect(() => {
    if (formRef.current && state) {
      formRef.current.setFieldsValue({
        palavra: state.palavra ?? "",
        descricao: state.descricao ?? "",
        uid: state.uid,
        letra: state.letra,
        categoria: state.categoria ?? "",
      });
    }
  }, [state]);

  if (!state?.uid) {
    return (
      <MobileLayout headerTitle="Edição" headerType="private">
        <Main style={{ alignItems: "center", justifyContent: "center" }}>
          <Empty description="Verbete inexistente" />
          <Button type="primary" onClick={() => navigate("/sinalario")}>
            Voltar ao Sinalário
          </Button>
        </Main>
      </MobileLayout>
    );
  }

  const currentVisibleImage = newImage;
  const currentVisibleVideo = newVideo;

  return (
    <MobileLayout headerTitle="Editar Verbete" headerType="private">
      <Main>
        <FormHeader>
          <h1>Manter Verbete</h1>
          <p>Altere os textos ou substitua as mídias anexas</p>
        </FormHeader>

        <Form ref={formRef} onFinish={handleEditVerbete} layout="vertical">
          <FormSection>
            <Form.Item
              label="Palavra"
              name="palavra"
              rules={[{ required: true, message: "Obrigatório" }]}
            >
              <Input size="large" placeholder="Insira a palavra do verbete" />
            </Form.Item>

            <Form.Item
              label="Família Alfabética"
              name="letra"
              rules={[{ required: true, message: "Obrigatório" }]}
            >
              <Select size="large">
                {Array.from("ABCDEFGHIJKLMNOPQRSTUVWXYZ").map((c) => (
                  <Option key={c} value={c}>
                    {c}
                  </Option>
                ))}
              </Select>
            </Form.Item>

            <Form.Item label="Categoria (Opcional)" name="categoria">
              <Input size="large" placeholder="Ex: Saudações, Família, etc." />
            </Form.Item>

            <Form.Item
              label="Significado / Descrição"
              name="descricao"
              rules={[{ required: true, message: "Obrigatório" }]}
            >
              <TextArea
                placeholder="Detalhes ou significado..."
                autoSize={{ minRows: 4, maxRows: 8 }}
                showCount
                maxLength={500}
              />
            </Form.Item>
          </FormSection>

          <FormSection style={{ marginTop: "1.6rem" }}>
            <MediaUploadSection
              style={{ borderTop: 0, paddingTop: 0, marginTop: 0 }}
            >
              <div className="ant-form-item-label">
                {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
                <label htmlFor="videoUpload">Sinal em Vídeo</label>
              </div>
              {currentVisibleVideo && (
                <MediaPreviewSquare>
                  <video
                    src={currentVisibleVideo}
                    controls
                    preload="metadata"
                    playsInline
                  >
                    <track kind="captions" />
                  </video>
                </MediaPreviewSquare>
              )}
              <Form.Item style={{ marginBottom: 0 }}>
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "8px",
                    width: "100%",
                  }}
                >
                  <Input
                    placeholder="Insira a URL do vídeo ou anexe um novo arquivo"
                    value={newVideo}
                    onChange={(e) => setNewVideo(e.target.value)}
                    style={{ height: "4.8rem", borderRadius: "0.8rem" }}
                  />
                  <Upload
                    id="videoUpload"
                    accept=".mp4, .mov, .avi"
                    maxCount={1}
                    customRequest={(options) => {
                      customUploadVideo(options).catch(console.error);
                    }}
                    showUploadList={false}
                  >
                    <Button
                      size="large"
                      icon={<DocumentUpload size={18} />}
                      style={{
                        width: "100%",
                        borderRadius: "0.8rem",
                        height: "4.8rem",
                      }}
                    >
                      {currentVisibleVideo
                        ? "Trocar Vídeo (Upload)"
                        : "Adicionar Vídeo (Upload)"}
                    </Button>
                  </Upload>
                </div>
              </Form.Item>
            </MediaUploadSection>

            <MediaUploadSection>
              <div className="ant-form-item-label">
                {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
                <label htmlFor="imageUpload">Mídia Gráfica (Imagem)</label>
              </div>
              {currentVisibleImage && (
                <MediaPreviewSquare>
                  <img src={currentVisibleImage} alt="Preview" loading="lazy" />
                </MediaPreviewSquare>
              )}
              <Form.Item style={{ marginBottom: 0 }}>
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "8px",
                    width: "100%",
                  }}
                >
                  <Input
                    placeholder="Insira a URL ou anexe uma nova imagem"
                    value={newImage}
                    onChange={(e) => setNewImage(e.target.value)}
                    style={{ height: "4.8rem", borderRadius: "0.8rem" }}
                  />
                  <Upload
                    id="imageUpload"
                    accept=".png, .jpg, .jpeg"
                    maxCount={1}
                    customRequest={(options) => {
                      customUploadImage(options).catch(console.error);
                    }}
                    showUploadList={false}
                  >
                    <Button
                      size="large"
                      icon={<DocumentUpload size={18} />}
                      style={{
                        width: "100%",
                        borderRadius: "0.8rem",
                        height: "4.8rem",
                      }}
                    >
                      {currentVisibleImage
                        ? "Trocar Imagem (Upload)"
                        : "Adicionar Imagem (Upload)"}
                    </Button>
                  </Upload>
                </div>
              </Form.Item>
            </MediaUploadSection>
          </FormSection>

          <FormFooter>
            <div className="warning-box">
              <Warning2
                size={24}
                color="var(--color-warning)"
                variant="Bulk"
                style={{ flexShrink: 0 }}
              />
              <p>
                As alterações feitas aqui substituirão os dados antigos
                permanentemente no sistema.
              </p>
            </div>

            <div className="action-buttons">
              <Button onClick={() => navigate(-1)} size="large">
                Descartar
              </Button>
              <Button
                type="primary"
                htmlType="submit"
                disabled={loadingVerbetes}
                style={{ backgroundColor: "var(--color-primary)", border: 0 }}
              >
                Salvar Alterações
              </Button>
            </div>
          </FormFooter>
        </Form>
      </Main>
    </MobileLayout>
  );
}

export default EditVerbete;
